import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import { articles as allArticles } from '@/mocks/articles';
import { categories, categoryMap } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || '');

  const currentCategory = activeSlug ? categoryMap[activeSlug] : null;

  const filteredArticles = useMemo(() => {
    if (!activeSlug) return allArticles;
    return allArticles.filter((a) => a.categoryId === activeSlug);
  }, [activeSlug]);

  const pageTitle = currentCategory ? `${currentCategory.name} - 分类` : '全部内容 - 分类';
  const pageDesc = currentCategory
    ? currentCategory.description
    : '久久热全部精彩内容，覆盖娱乐、资讯、生活、科技、体育、时尚等多元领域。';

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://jiujiure.com/category/${activeSlug || ''}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '久久热',
      url: 'https://jiujiure.com',
    },
  };

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '分类' },
    ...(currentCategory ? [{ label: currentCategory.name }] : []),
  ];

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`久久热${currentCategory ? ',' + currentCategory.name : ''},久久热分类,热门内容`}
        canonicalUrl={`https://jiujiure.com/category/${activeSlug}`}
        schemaJson={categorySchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <Breadcrumb items={breadcrumbItems} />
          <div className="w-full px-4 md:px-6 pb-6">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-2">
                {currentCategory ? currentCategory.name : '全部内容'}
              </h1>
              {currentCategory && (
                <p className="mt-2 text-sm text-foreground-600">
                  {currentCategory.description}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-7xl mx-auto">
            <div className="mb-6">
              <CategoryTabs
                activeSlug={activeSlug}
                onSelect={(newSlug) => setActiveSlug(newSlug)}
              />
            </div>

            {filteredArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {filteredArticles.map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    title={article.title}
                    excerpt={article.excerpt}
                    coverImage={article.coverImage}
                    categoryId={article.categoryId}
                    categoryName={categoryMap[article.categoryId]?.name || ''}
                    author={article.author}
                    publishedAt={article.publishedAt}
                    viewCount={article.viewCount}
                    isHot={article.isHot}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-200">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400" />
                </span>
                <p className="mt-4 text-foreground-500">暂无相关文章</p>
                <Link
                  to="/"
                  className="mt-4 inline-block text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                >
                  返回首页
                </Link>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}